/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import JoiBrowser from "joi-browser"; // Npm: Joi for frontend validation.
import _ from "underscore"; // Npm: Underscore utility module.
import Editor from "@monaco-editor/react"; // Npm: Monaco editor react wrapper.
import { useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { toast } from "react-hot-toast"; // Npm: Toast notifications.
import { useHistory, useParams } from "react-router-dom"; // Npm: React router dom.
import {
  HiArrowLeft,
  HiCheckCircle,
  HiCodeBracketSquare,
  HiDocumentText,
  HiExclamationTriangle,
  HiSparkles,
} from "react-icons/hi2"; // Npm: React icons.
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import { MemoizedInput } from "components/MemoizedInput";
import SubmitButton from "components/SubmitButton";

/*
 * GRAPHS
 */
import ScriptReadQuery from "../__query__/index.script.read.query";
import ScriptCreateMutation from "components/PduScriptUpsert/__mutation__/index.script.create.mutation";
import ScriptUpdateMutation from "components/PduScriptUpsert/__mutation__/index.script.update.mutation";

/*
 * STYLES
 */
import { buttonStyle } from "../index.style";

/*
 * HELPERS
 */
const _FormatEditorMarker = (__marker) =>
  `Line ${__marker.startLineNumber}: ${__marker.message}`;

const _ValidateJavaScriptWithRuntime = (__scriptCode) => {
  try {
    // Validates syntax without executing the submitted script.
    Function(__scriptCode);

    return void 0;
  } catch (caughtError) {
    return {
      message: caughtError?.message || "Invalid JavaScript syntax.",
      line: caughtError?.lineNumber,
    };
  }
};

const _GetScriptLineCount = (__scriptCode) =>
  (__scriptCode || "").split(/\r\n|\r|\n/).length;

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const { scriptId } = useParams();
  const isEditMode = Boolean(scriptId);
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const _historyRef = useHistory();
  const _editorRef = React.useRef(null);
  const _monacoRef = React.useRef(null);
  const _isSubmitInProgressRef = React.useRef(false);
  const [formData, setFormData] = React.useState({
    name: "",
    scriptCode: "",
  });
  const [formError, setFormError] = React.useState("");
  const [syntaxError, setSyntaxError] = React.useState("");
  const [editorMarkers, setEditorMarkers] = React.useState([]);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });
  const _QueryScriptRead = useQuery(ScriptReadQuery, {
    variables: { scriptId },
    fetchPolicy: Object.React.App.fetchPolicy,
    skip: !isEditMode,
  });
  const [MutationScriptCreate, MutationScriptCreateResponse] = useMutation(
    ScriptCreateMutation,
  );
  const [MutationScriptUpdate, MutationScriptUpdateResponse] = useMutation(
    ScriptUpdateMutation,
  );

  // Event handler.
  const _HandleInputChange = React.useCallback(({ target }) => {
    setFormData((current) => ({ ...current, [target.name]: target.value }));
  }, []);
  const _HandleEditorMount = React.useCallback((__editor, __monaco) => {
    _editorRef.current = __editor;
    _monacoRef.current = __monaco;
    __editor.focus();
    __monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    });
  }, []);
  const _HandleFormatDocument = React.useCallback(() => {
    _editorRef.current?.getAction("editor.action.formatDocument")?.run();
  }, []);
  const _HandleEditorValidate = React.useCallback((markers) => {
    const _errorMarkers = markers.filter(
      (i) => i.severity === _monacoRef.current?.MarkerSeverity?.Error,
    );

    setEditorMarkers((currentMarkers) =>
      _.isEqual(currentMarkers, _errorMarkers) ? currentMarkers : _errorMarkers,
    );
  }, []);
  const _GetEditorSyntaxErrors = React.useCallback(() => {
    const _monaco = _monacoRef.current;
    const _editor = _editorRef.current;

    if (!_monaco || !_editor?.getModel) return editorMarkers;

    return _monaco.editor
      .getModelMarkers({ resource: _editor.getModel().uri })
      .filter((i) => i.severity === _monaco.MarkerSeverity.Error);
  }, [editorMarkers]);
  const _ValidateJavaScriptSyntax = React.useCallback(() => {
    const _marker = _.first(_GetEditorSyntaxErrors());

    if (_marker) return _FormatEditorMarker(_marker);

    const _runtimeError = _ValidateJavaScriptWithRuntime(
      formData.scriptCode,
    );

    if (_runtimeError)
      return _runtimeError.line
        ? `Line ${_runtimeError.line}: ${_runtimeError.message}`
        : _runtimeError.message;

    return "";
  }, [_GetEditorSyntaxErrors, formData.scriptCode]);
  const _SubmitForm = React.useCallback(
    async (event) => {
      event.preventDefault();

      if (_isSubmitInProgressRef.current) return void 0;

      setFormError("");
      setSyntaxError("");

      const _submittedFormData = Object.fromEntries(
        new FormData(event.currentTarget).entries(),
      );
      const _formData = {
        ...formData,
        ..._submittedFormData,
        scriptCode: formData.scriptCode,
      };
      const _JoiSchema = JoiBrowser.object({
        name: JoiBrowser.string().trim().required().label("PDU Name"),
        scriptCode: JoiBrowser.string().required().label("Script Code"),
      }).options({ allowUnknown: true });
      const _validation = _JoiSchema.validate(_formData);

      if (_validation.error)
        return setFormError(_validation.error?.message);

      const _scriptSyntaxError = _ValidateJavaScriptSyntax();

      if (_scriptSyntaxError) return setSyntaxError(_scriptSyntaxError);

      try {
        _isSubmitInProgressRef.current = true;
        let _successToastMessage;
        let _successToastId;

        if (isEditMode) {
          const _updateResponse = await MutationScriptUpdate({
            variables: {
              scriptId,
              name: _formData.name,
              scriptCode: _formData.scriptCode,
            },
          });
          const _updatedScript = _updateResponse?.data?.ScriptUpdate;

          if (!_successFlags.includes(_updatedScript?.status)) {
            toast(_updatedScript?.message);

            return void 0;
          }

          _successToastMessage = _updatedScript?.message;
          _successToastId = `pdu-script-update-${_updatedScript?.id || scriptId}`;
        } else {
          const _createResponse = await MutationScriptCreate({
            variables: {
              name: _formData.name,
              scriptCode: _formData.scriptCode,
            },
          });
          const _createdScript = _createResponse?.data?.ScriptCreate;

          if (
            !_createdScript?.id ||
            !_successFlags.includes(_createdScript?.status)
          ) {
            toast(_createdScript?.message);

            return void 0;
          }

          _successToastMessage = _createdScript?.message;
          _successToastId = `pdu-script-create-${_createdScript.id}`;
        }

        _historyRef.push("/pdu-script");
        setTimeout(() => {
          if (_successToastMessage)
            toast(_successToastMessage, { id: _successToastId });
        }, 0);

        return void 0;
      } catch (caughtError) {
        toast(
          caughtError?.message ||
            "Something went wrong please try after some time.",
        );

        return caughtError;
      } finally {
        _isSubmitInProgressRef.current = false;
      }
    },
    [
      MutationScriptCreate,
      MutationScriptUpdate,
      _ValidateJavaScriptSyntax,
      _historyRef,
      _successFlags,
      formData,
      isEditMode,
      scriptId,
    ],
  );

  React.useEffect(() => {
    if (!isEditMode) {
      setFormData({
        name: "",
        scriptCode: "",
      });
      setFormError("");
      setSyntaxError("");
    }
  }, [isEditMode]);
  React.useEffect(() => {
    const _script = _.first(_QueryScriptRead.data?.ScriptRead);

    if (!isEditMode || _.isEmpty(_script)) return void 0;

    setFormData({
      name: _script.name || "",
      scriptCode: _script.scriptCode || "",
    });
    setFormError("");
    setSyntaxError("");
  }, [_QueryScriptRead.data?.ScriptRead, isEditMode]);

  // Const assignment.
  const _isLoading =
    MutationScriptCreateResponse.loading ||
    MutationScriptUpdateResponse.loading;
  const _isInputDisabled = _isLoading || _QueryScriptRead.loading;
  const _editorHeight = _isCurrentViewMobile
    ? "calc(100vh - 330px)"
    : "calc(100vh - 285px)";
  const _currentEditorMarker = _.first(editorMarkers);
  const _activeSyntaxMessage =
    syntaxError ||
    (_currentEditorMarker ? _FormatEditorMarker(_currentEditorMarker) : "");
  const _scriptLineCount = _GetScriptLineCount(formData.scriptCode);

  // Return component.
  return (
    <form
      style={{ width: "100%" }}
      onSubmit={_SubmitForm}
    >
      <Flex
        bg="#f4f7fe"
        border="1px solid"
        borderColor="rgba(226, 232, 240, 0.9)"
        borderRadius="16px"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.12)"
        fontFamily="Dm Sans"
        minH={_isCurrentViewMobile ? "auto" : "calc(100vh - 155px)"}
        overflow="hidden"
        gap="0"
        flexDir="column"
        w="100%"
      >
        <Flex
          align="center"
          justify="space-between"
          bg="white"
          borderBottom="1px solid"
          borderColor="rgba(226, 232, 240, 0.95)"
          flexWrap="wrap"
          gap="14px"
          px={_isCurrentViewMobile ? "14px" : "22px"}
          py="16px"
        >
          <Flex align="center" gap="14px" minW="0">
            <Tooltip label="Back to PDU scripts" fontSize="sm">
              <Button
                aria-label="Back to PDU scripts"
                minW="42px"
                h="42px"
                px="0"
                border="1px solid"
                borderColor="purple.100"
                boxShadow="0 8px 18px rgba(117, 81, 255, 0.08)"
                onClick={() => _historyRef.push("/pdu-script")}
                leftIcon={<HiArrowLeft />}
                style={buttonStyle}
              />
            </Tooltip>
            <Box minW="0">
              <Flex align="center" gap="10px" flexWrap="wrap">
                <Text
                  color="#1b2559"
                  fontFamily="DM Sans"
                  fontSize={_isCurrentViewMobile ? "16px" : "18px"}
                  fontWeight="700"
                  letterSpacing="0"
                  lineHeight="22px"
                >
                  PDU Script Editor
                </Text>
                <Badge
                  bg={isEditMode ? "purple.50" : "green.50"}
                  color={isEditMode ? "purple.700" : "green.700"}
                  border="1px solid"
                  borderColor={isEditMode ? "purple.100" : "green.100"}
                  borderRadius="7px"
                  fontFamily="DM Sans"
                  fontSize="10px"
                  fontWeight="700"
                  lineHeight="14px"
                  px="7px"
                  py="2px"
                  textTransform="none"
                >
                  {isEditMode ? "Edit mode" : "Create mode"}
                </Badge>
              </Flex>
              <Flex align="center" color="#8F9BBA" gap="6px" mt="4px">
                <HiSparkles size={13} />
                <Text
                  color="#8F9BBA"
                  fontFamily="DM Sans"
                  fontSize="11px"
                  fontWeight="500"
                  lineHeight="16px"
                >
                  JavaScript workspace for wholesale PDU processing
                </Text>
              </Flex>
            </Box>
          </Flex>
          <SubmitButton
            disabled={_isInputDisabled}
            isLoading={_isLoading}
            defaultText={isEditMode ? "Update Script" : "Create Script"}
            mt="0"
            h="42px"
            minW={_isCurrentViewMobile ? "100%" : "150px"}
            boxShadow="0 10px 20px rgba(117, 81, 255, 0.22)"
            icon={<HiCodeBracketSquare size={16} />}
          />
        </Flex>

        {isEditMode && _QueryScriptRead.loading ? (
          <Flex align="center" justify="center" minH="460px" bg="white">
            <Spinner color="purple.500" />
          </Flex>
        ) : (
          <Flex
            flexDir="column"
            gap="16px"
            p={_isCurrentViewMobile ? "12px" : "18px"}
          >
            <Flex
              bg="white"
              border="1px solid"
              borderColor="rgba(226, 232, 240, 0.95)"
              borderRadius="14px"
              boxShadow="0 12px 28px rgba(112, 144, 176, 0.09)"
              gap="16px"
              flexDir={{ base: "column", md: "row" }}
              align={{ base: "stretch", md: "flex-start" }}
              p={_isCurrentViewMobile ? "10px" : "12px"}
            >
              <Box flex="1">
                <MemoizedInput
                  isRequired={true}
                  disabled={_isInputDisabled}
                  name="name"
                  label="PDU Name"
                  placeholder='e.g. "PDU Script Name"'
                  onChange={_HandleInputChange}
                  isInvalid={
                    formError?.includes("name") ||
                    formError?.includes("PDU Name")
                  }
                  error={formError}
                  value={formData.name}
                  minH="34px"
                  h="34px"
                  fontSize="14px"
                  borderRadius="10px"
                />
              </Box>
              <Flex
                align="center"
                alignSelf={{ base: "stretch", md: "center" }}
                bg="#f7f9ff"
                border="1px solid"
                borderColor="rgba(226, 232, 240, 0.95)"
                borderRadius="12px"
                color="#1b2559"
                gap="10px"
                minH="42px"
                px="12px"
                flexShrink="0"
              >
                <Flex
                  align="center"
                  justify="center"
                  bg="white"
                  border="1px solid"
                  borderColor="purple.100"
                  borderRadius="10px"
                  color="purple.600"
                  h="30px"
                  w="30px"
                >
                  <HiDocumentText size={17} />
                </Flex>
                <Box>
                  <Text color="gray.500" fontSize="11px" fontWeight="600">
                    Runtime
                  </Text>
                  <Text color="#1b2559" fontSize="13px" fontWeight="700">
                    JavaScript PDU
                  </Text>
                </Box>
              </Flex>
            </Flex>

            <Box
              border="1px solid"
              borderColor={_activeSyntaxMessage ? "red.300" : "#222837"}
              borderRadius="14px"
              overflow="hidden"
              minH="460px"
              h={_editorHeight}
              bg="#0f1117"
              boxShadow="0 20px 45px rgba(15, 23, 42, 0.22)"
            >
              <Flex
                align="center"
                justify="space-between"
                bg="#151923"
                borderBottom="1px solid"
                borderColor="#2a3040"
                color="gray.200"
                minH="54px"
                px={_isCurrentViewMobile ? "12px" : "16px"}
                gap="12px"
                flexWrap="wrap"
              >
                <Flex align="center" gap="10px" minW="0">
                  <Flex
                    align="center"
                    justify="center"
                    bg="rgba(117, 81, 255, 0.16)"
                    border="1px solid"
                    borderColor="rgba(117, 81, 255, 0.28)"
                    borderRadius="10px"
                    color="#b9a8ff"
                    h="34px"
                    w="34px"
                    flexShrink="0"
                  >
                    <HiCodeBracketSquare size={18} />
                  </Flex>
                  <Box minW="0">
                    <Text
                      color="gray.100"
                      fontSize="14px"
                      fontWeight="700"
                      lineHeight="18px"
                      noOfLines={1}
                    >
                      {formData.name
                        ? `${formData.name}.js`
                        : "untitled-pdu-script.js"}
                    </Text>
                    <Flex align="center" color="gray.500" gap="8px">
                      <Text fontSize="11px" lineHeight="14px">
                        JavaScript
                      </Text>
                      <Text color="#50596b" fontSize="11px">
                        |
                      </Text>
                      <Text fontSize="11px" lineHeight="14px">
                        {_scriptLineCount} lines
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
                <Flex align="center" gap="8px" flexShrink="0">
                  <Button
                    bg="#222838"
                    border="1px solid"
                    borderColor="#333b4f"
                    borderRadius="8px"
                    color="gray.200"
                    fontSize="12px"
                    fontWeight="700"
                    h="28px"
                    minW="82px"
                    leftIcon={<HiCodeBracketSquare size={14} />}
                    onClick={_HandleFormatDocument}
                    _hover={{ bg: "#2b3346" }}
                  >
                    Format
                  </Button>
                  <Badge
                    bg="#202738"
                    border="1px solid"
                    borderColor="#30384b"
                    color="gray.300"
                    borderRadius="8px"
                    fontSize="11px"
                    fontWeight="700"
                    px="8px"
                    py="4px"
                    textTransform="none"
                  >
                    Ctrl+F
                  </Badge>
                </Flex>
              </Flex>
              <Editor
                height="calc(100% - 108px)"
                defaultLanguage="javascript"
                language="javascript"
                theme="vs-dark"
                value={formData.scriptCode}
                loading={<Spinner color="purple.500" />}
                onMount={_HandleEditorMount}
                onValidate={_HandleEditorValidate}
                onChange={(value) => {
                  setSyntaxError("");
                  setFormData((current) => ({
                    ...current,
                    scriptCode: value || "",
                  }));
                }}
                options={{
                  automaticLayout: true,
                  autoIndent: "full",
                  bracketPairColorization: { enabled: true },
                  cursorBlinking: "smooth",
                  detectIndentation: true,
                  fixedOverflowWidgets: true,
                  folding: true,
                  formatOnPaste: true,
                  formatOnType: true,
                  glyphMargin: true,
                  lineNumbers: "on",
                  matchBrackets: "always",
                  minimap: { enabled: false },
                  overviewRulerBorder: false,
                  padding: { top: 14, bottom: 14 },
                  renderLineHighlight: "all",
                  renderValidationDecorations: "on",
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  suggestOnTriggerCharacters: true,
                  wordWrap: "on",
                }}
              />
              <Flex
                align="center"
                justify="space-between"
                bg="#151923"
                borderTop="1px solid"
                borderColor="#2a3040"
                color={_activeSyntaxMessage ? "red.200" : "green.200"}
                fontSize="12px"
                gap="8px"
                minH="54px"
                px={_isCurrentViewMobile ? "12px" : "16px"}
                py="8px"
              >
                <Flex align="center" gap="8px" minW="0">
                  {_activeSyntaxMessage ? (
                    <HiExclamationTriangle size={16} />
                  ) : (
                    <HiCheckCircle size={16} />
                  )}
                  <Text fontWeight="600" noOfLines={2}>
                    {_activeSyntaxMessage ||
                      "No JavaScript syntax errors detected."}
                  </Text>
                </Flex>
                <Text
                  color="gray.500"
                  display={_isCurrentViewMobile ? "none" : "block"}
                  fontSize="11px"
                  fontWeight="600"
                  flexShrink="0"
                >
                  Auto layout enabled
                </Text>
              </Flex>
            </Box>
          </Flex>
        )}
      </Flex>
    </form>
  );
};

/*
 * EXPORT
 */
export default React.memo(Index);
